import React, { useEffect, useState } from 'react';
import Spinner from '../../Spinner';
import SubscriptionStatusBadge from './SubscriptionStatusBadge';
import { fetchUserSubscriptionDetails } from '../../../services/admin/subscriptionTrackerApi';
import {
  SportSubscriptionDetail,
  TeamSportSubscriptionDetail,
  TeamSubscriptionSummary,
  UserSubscriptionDetailResponse,
} from '../../../types/admin/subscriptionTracker';
import {
  formatDaysRemaining,
  formatSubscriptionDate,
  getSportLabel,
} from '../../../utils/admin/formatSubscriptionDate';

export const NO_TEAM_SPORT_SUBSCRIPTION_MESSAGE =
  'No sports subscription for this team yet.';

const TRIAL_USER_STATUSES = new Set(['trial', 'trial_end']);

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; error: string }
  | { status: 'success'; data: UserSubscriptionDetailResponse };

/** Session cache shared by team header + per-user rows. */
const detailsCache = new Map<number, UserSubscriptionDetailResponse>();

function useUserSubscriptionDetails(userId: number): LoadState {
  const [state, setState] = useState<LoadState>(() => {
    const cached = detailsCache.get(userId);
    return cached ? { status: 'success', data: cached } : { status: 'loading' };
  });

  useEffect(() => {
    const cached = detailsCache.get(userId);
    if (cached) {
      setState({ status: 'success', data: cached });
      return;
    }

    const controller = new AbortController();
    setState({ status: 'loading' });

    void (async () => {
      const result = await fetchUserSubscriptionDetails(userId, { signal: controller.signal });
      if (result.aborted || controller.signal.aborted) return;

      if (result.data) {
        detailsCache.set(userId, result.data);
        setState({ status: 'success', data: result.data });
      } else {
        setState({ status: 'error', error: result.error ?? 'Failed to load plan' });
      }
    })();

    return () => {
      controller.abort();
    };
  }, [userId]);

  return state;
}

function SportChip({
  code,
  status,
  daysRemaining,
  endDate,
  extra,
}: {
  code: string;
  status: string;
  daysRemaining?: number | null;
  endDate?: string | null;
  extra?: React.ReactNode;
}) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-800">
      <span className="font-medium">{getSportLabel(code)}</span>
      <SubscriptionStatusBadge status={status} />
      {extra}
      {daysRemaining != null && (
        <span className="text-gray-500">{formatDaysRemaining(daysRemaining)}</span>
      )}
      {endDate && (
        <span className="text-gray-400">· ends {formatSubscriptionDate(endDate)}</span>
      )}
    </span>
  );
}

function TeamSportsChips({
  sports,
  emptyMessage = NO_TEAM_SPORT_SUBSCRIPTION_MESSAGE,
}: {
  sports: TeamSportSubscriptionDetail[];
  emptyMessage?: string;
}) {
  if (sports.length === 0) {
    return <span className="text-xs text-slate-500">{emptyMessage}</span>;
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {sports.map((sport) => {
        const displayStatus =
          sport.display_status ||
          (sport.status === 'subscribed' && sport.is_grace ? 'grace' : sport.status);
        return (
          <SportChip
            key={sport.sport_code}
            code={sport.sport_code}
            status={displayStatus}
            daysRemaining={sport.days_remaining}
            endDate={sport.end_date}
            extra={
              sport.is_grace ? (
                <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                  Grace
                </span>
              ) : null
            }
          />
        );
      })}
    </div>
  );
}

function UserSportsChips({ sports }: { sports: SportSubscriptionDetail[] }) {
  if (sports.length === 0) {
    return <span className="text-xs text-gray-400">No user subscription</span>;
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {sports.map((sport) => (
        <SportChip
          key={sport.sport_code}
          code={sport.sport_code}
          status={sport.status}
          daysRemaining={sport.days_remaining}
          endDate={sport.end_date}
        />
      ))}
    </div>
  );
}

function formatWiredSportsList(codes: string[]): string {
  const normalized = codes.map((c) => String(c || '').trim()).filter(Boolean);
  if (normalized.length === 0) return '—';
  return normalized.map((code) => getSportLabel(code)).join(', ');
}

function resolveDisplayStatus(sport: TeamSportSubscriptionDetail): string {
  return (
    sport.display_status ||
    (sport.status === 'subscribed' && sport.is_grace ? 'grace' : sport.status)
  );
}

type SportTile = {
  sport_code: string;
  displayStatus: string;
  endDate: string | null;
  daysRemaining: number | null;
  isGrace: boolean;
};

function buildSportTiles(
  wiredSportCodes: string[],
  teamSports: TeamSportSubscriptionDetail[]
): SportTile[] {
  const byCode = new Map(
    teamSports.map((sport) => [String(sport.sport_code || '').toUpperCase(), sport])
  );
  const codes =
    wiredSportCodes.length > 0
      ? wiredSportCodes
      : teamSports.map((s) => s.sport_code);

  return codes.map((code) => {
    const normalized = String(code || '').toUpperCase();
    const sport = byCode.get(normalized);
    if (!sport) {
      return {
        sport_code: normalized,
        displayStatus: '',
        endDate: null,
        daysRemaining: null,
        isGrace: false,
      };
    }
    return {
      sport_code: normalized,
      displayStatus: resolveDisplayStatus(sport),
      endDate: sport.end_date,
      daysRemaining: sport.days_remaining,
      isGrace: Boolean(sport.is_grace),
    };
  });
}

function TeamSubscriptionSportTile({ tile }: { tile: SportTile }) {
  const hasPlan = Boolean(tile.displayStatus && tile.displayStatus.toLowerCase() !== 'n/a');

  return (
    <div className="flex h-full min-h-[5.25rem] flex-col justify-between rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-2.5 text-xs">
      <p className="font-semibold text-gray-900 leading-snug">{getSportLabel(tile.sport_code)}</p>
      <div className="mt-2 space-y-1">
        {hasPlan ? (
          <>
            <SubscriptionStatusBadge status={tile.displayStatus} />
            {tile.endDate ? (
              <p className="text-gray-500">ends {formatSubscriptionDate(tile.endDate)}</p>
            ) : (
              <p className="text-gray-400">—</p>
            )}
          </>
        ) : (
          <p className="text-gray-400">Not subscribed</p>
        )}
      </div>
    </div>
  );
}

function TeamSubscriptionHeaderBody({
  activeSubCount,
  wiredSportCount,
  wiredSportCodes = [],
  teamSports,
  loading = false,
}: {
  activeSubCount: number;
  wiredSportCount: number;
  wiredSportCodes?: string[];
  teamSports: TeamSportSubscriptionDetail[];
  loading?: boolean;
}) {
  if (loading) {
    return (
      <div className="mt-4 min-h-[6rem] flex items-center gap-2 text-sm text-slate-600">
        <Spinner size="xs" />
        <span>Loading team subscription…</span>
      </div>
    );
  }

  const totalSports = wiredSportCount > 0 ? wiredSportCount : wiredSportCodes.length;
  const tiles = buildSportTiles(wiredSportCodes, teamSports);

  return (
    <div className="mt-4 space-y-3">
      <p className="text-sm text-gray-700">
        <span className="font-medium text-gray-600">Team sports:</span>{' '}
        <span className="text-gray-900">{formatWiredSportsList(wiredSportCodes)}</span>
      </p>

      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
        <span className="font-medium text-gray-600">Subscriptions:</span>
        <span className="inline-flex items-center rounded-md bg-gray-100 px-2.5 py-0.5 text-sm font-semibold tabular-nums text-gray-800">
          {activeSubCount}/{totalSports > 0 ? totalSports : '—'}
        </span>
      </div>

      {tiles.length === 0 ? (
        <p className="text-sm text-gray-500">{NO_TEAM_SPORT_SUBSCRIPTION_MESSAGE}</p>
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {tiles.map((tile) => (
            <TeamSubscriptionSportTile key={tile.sport_code} tile={tile} />
          ))}
        </div>
      )}
    </div>
  );
}

function TeamSubscriptionHeaderPlaceholder() {
  return (
    <div className="mt-4 space-y-3" aria-hidden>
      <div className="h-4 w-2/3 max-w-md rounded bg-slate-100" />
      <div className="h-6 w-16 rounded bg-slate-100" />
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="min-h-[5.25rem] rounded-lg bg-slate-50 border border-slate-100" />
        ))}
      </div>
    </div>
  );
}

interface InlineTeamPlanDetailsProps {
  /** Preloaded from team-summaries API (preferred). */
  summary?: TeamSubscriptionSummary | null;
  /** True while team-summaries request is in flight. */
  summariesLoading?: boolean;
  /** Queued for lazy load (User Sub list). */
  summaryPending?: boolean;
  /** Fallback: load team_sports via detail API when summary missing. */
  sampleUserId?: number;
}

function InlineTeamPlanDetailsFromUser({ userId }: { userId: number }) {
  const state = useUserSubscriptionDetails(userId);

  if (state.status === 'loading') {
    return (
      <div className="mt-2 flex items-center gap-2 text-xs text-slate-600">
        <Spinner size="xs" />
        <span>Loading team subscription…</span>
      </div>
    );
  }

  if (state.status === 'error') {
    return <p className="mt-2 text-xs text-red-600">{state.error}</p>;
  }

  const teamSports = state.data.team_sports ?? [];
  const activeSubCount = teamSports.filter((sport) => {
    const display = (sport.display_status || sport.status || '').toLowerCase();
    return display === 'subscribed' || display === 'grace';
  }).length;

  return (
    <TeamSubscriptionHeaderBody
      activeSubCount={activeSubCount}
      wiredSportCount={teamSports.length}
      wiredSportCodes={teamSports.map((s) => s.sport_code)}
      teamSports={teamSports}
    />
  );
}

/** Team subscription block shown under the team name (once per team). */
export const InlineTeamPlanDetails: React.FC<InlineTeamPlanDetailsProps> = ({
  summary,
  summariesLoading = false,
  summaryPending = false,
  sampleUserId,
}) => {
  if (summary) {
    return (
      <TeamSubscriptionHeaderBody
        activeSubCount={summary.active_sub_count}
        wiredSportCount={summary.wired_sport_count}
        wiredSportCodes={summary.wired_sport_codes ?? []}
        teamSports={summary.team_sports ?? []}
      />
    );
  }

  if (summariesLoading || summaryPending) {
    return <TeamSubscriptionHeaderBody loading teamSports={[]} activeSubCount={0} wiredSportCount={0} />;
  }

  if (sampleUserId == null) {
    return <TeamSubscriptionHeaderPlaceholder />;
  }

  return <InlineTeamPlanDetailsFromUser userId={sampleUserId} />;
};

interface InlineUserPlanDetailsProps {
  userId: number;
}

/** User subscription only — under each user row. */
const InlineUserPlanDetails: React.FC<InlineUserPlanDetailsProps> = ({ userId }) => {
  const state = useUserSubscriptionDetails(userId);

  if (state.status === 'loading') {
    return (
      <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
        <Spinner size="xs" />
        <span>Loading user subscription…</span>
      </div>
    );
  }

  if (state.status === 'error') {
    return <p className="mt-2 text-xs text-red-600">{state.error}</p>;
  }

  const userSports = (state.data.user_sports ?? []).filter((s) =>
    TRIAL_USER_STATUSES.has(s.status?.toLowerCase() ?? '')
  );

  return (
    <div className="mt-2 border-l-2 border-blue-200 pl-3">
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-blue-600">
        User subscription
      </div>
      <UserSportsChips sports={userSports} />
    </div>
  );
};

export default InlineUserPlanDetails;
